import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import StripePackages from '../../components/payments/StripePackages';
import { 
  Loader2, CreditCard, Clock, CheckCircle2, TrendingUp
} from 'lucide-react';
import api from '../../lib/api';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [stripePackages, setStripePackages] = useState([]);
  const [loadingStripe, setLoadingStripe] = useState(false);

  const loadPayments = useCallback(async () => {
    try {
      const res = await api.get('/payments/history');
      setPayments(res.data.payments || []);
    } catch (err) {
      console.error('Failed to load payments:', err);
    } finally {
      setLoadingPayments(false);
    }
  }, []);

  const loadStripePackages = useCallback(async () => {
    try {
      const res = await api.get('/payments/stripe/packages');
      setStripePackages(Object.entries(res.data.packages || {}).map(([id, pkg]) => ({
        id,
        ...pkg
      })));
    } catch (err) {
      console.error('Failed to load Stripe packages:', err);
    }
  }, []);

  useEffect(() => {
    loadPayments();
    loadStripePackages();
  }, [loadPayments, loadStripePackages]);

  const handleStripeCheckout = async (packageId) => {
    setLoadingStripe(true);
    try {
      const res = await api.post('/payments/stripe/create-checkout', {
        package_id: packageId
      });
      
      window.location.href = res.data.checkout_url;
    } catch (error) {
      console.error('Stripe checkout failed:', error);
      alert(error.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setLoadingStripe(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="payments-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-blue-400" />
            Payments
          </h1>
          <p className="text-slate-400 mt-1">Pay platform fees with credit card</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stripe Payment */}
          <div className="lg:col-span-2">
            <StripePackages
              packages={stripePackages}
              onCheckout={handleStripeCheckout}
              loading={loadingStripe}
            />
          </div>

          {/* Sidebar - Payment History */}
          <div className="space-y-6">
            <div className="stat-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Recent Payments
              </h3>

              {loadingPayments ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
                </div>
              ) : payments.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">
                  No payments yet
                </p>
              ) : (
                <div className="space-y-3">
                  {payments.slice(0, 5).map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {payment.status === 'completed' ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-400" />
                        )}
                        <div>
                          <p className="text-sm font-medium">
                            ${payment.amount?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {payment.description || 'Payment'}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        payment.status === 'completed'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {payment.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Card */}
            <div className="stat-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Quick Stats
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400 text-sm">Total Payments</span>
                  <span className="font-medium">{payments.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 text-sm">Completed</span>
                  <span className="font-medium text-emerald-400">
                    {payments.filter(p => p.status === 'completed').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 text-sm">Total Amount</span>
                  <span className="font-medium">
                    ${payments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
